/**
 * Email Campaign Editor Page
 *
 * Split-panel layout matching the PHP campaign edit.php:
 * - Left: campaign details form + contacts section (lists, direct contacts, excludes)
 * - Right: FtpEditor in preview mode with empty content overlay
 * - Header: back, status, send test, send/schedule, cancel, save, overflow menu
 */
import React, { useCallback, useEffect, useMemo, useState, useRef, Suspense } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, ChevronDown, Loader2, Save,
  Calendar, Users, X, MoreVertical, Copy,
  MailOpen, Search, Trash2, Mail, Send, Lock, Unlock,
  AlertTriangle, Lightbulb,
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { apiClient } from '@/services/apiClient';
import * as campaignsSvc from '@/services/campaigns.service';
import type { CampaignList } from '@/services/campaigns.service';
import { listEntryLists } from '@/services/marketing.service';
import { CloneCampaignModal } from './CloneCampaignModal';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatWallClockInTz, getTimezoneAbbreviation } from '@/lib/facilityTimezone';

import { FtpTemplatePicker, type FtpTemplateRow } from '@/components/ftp-editor/FtpTemplatePicker';
import EmailEditorChoiceModal from '@/components/email-editor-host/EmailEditorChoiceModal';
import { detectEditorType, type EmailEditorType } from '@/components/email-editor-host/editor-type';
import type { EmailEditorHostHandle } from '@/components/email-editor-host/EmailEditorHost';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { DateTimePicker, nowAsDateTimeValue } from '@/components/ui/DateTimePicker';

const EmailEditorHost = lazyWithRetry(() => import('@/components/email-editor-host/EmailEditorHost'), 'email-editor-host');

interface Sender {
  emailAccountSenderID: number;
  senderName: string;
  email: string;
  status: number;
  default: boolean;
  verified: boolean;
}

interface AvailableList {
  emailListId: number;
  name: string;
  listType: string;
  patientCount: number;
  uniqueEmailCount?: number;
  uniquePhoneCount?: number;
}

// `scheduledAt` here is the datetime-local value, a wall clock already in the
// account's zone, so it must be formatted as-is rather than re-read through
// the browser's zone. See `formatWallClockInTz`.
function formatScheduleDate(iso: string, tz: string): string {
  return formatWallClockInTz(iso, tz);
}

// The send pipeline appends an unsubscribe footer when neither token is present
// (SendGridService::ensureUnsubscribeFooter), so a missing link is a suggestion
// rather than a blocker. Match the tokens it looks for.
function hasUnsubscribeToken(html: string | null | undefined): boolean {
  if (!html) return false;
  return /\{\{\s*unsubscribe(_link)?\s*\}\}/i.test(html);
}

function isHtmlContentEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  const noStyle = html.replace(/<style[\s\S]*?<\/style>/gi, '');
  const noHead = noStyle.replace(/<head[\s\S]*?<\/head>/gi, '');
  const stripped = noHead.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  if (stripped.length > 0) return false;
  if (/<img\s/i.test(noHead)) return false;
  if (/<video\s/i.test(noHead)) return false;
  return true;
}

export default function EmailCampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user: authUser, hasPermission } = useAuth();
  const userTz = useUserTimezone();
  const tzAbbr = useMemo(() => getTimezoneAbbreviation(userTz), [userTz]);

  const editorRef = useRef<EmailEditorHostHandle | null>(null);
  const [viewAsLocked, setViewAsLocked] = useState(false);

  // ── Editor selection (DnD vs HTML) ──
  const [editorType, setEditorType] = useState<EmailEditorType>('html');
  const [editorSeed, setEditorSeed] = useState(0);
  const [editorInit, setEditorInit] = useState<{ html: string; plain: string; json: string | null }>({ html: '', plain: '', json: null });
  const [showEditorChoice, setShowEditorChoice] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // ── Fetch campaign ──
  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign-editor', id],
    queryFn: () => campaignsSvc.getCampaign(Number(id)),
    enabled: !!id,
  });

  // ── Fetch senders ──
  const { data: senders = [] } = useQuery({
    queryKey: ['marketing-senders'],
    queryFn: async () => {
      const { data } = await apiClient.get('/marketing/settings/senders');
      return (Array.isArray(data) ? data : data?.data ?? []) as Sender[];
    },
  });

  // ── Fetch campaign lists ──
  const { data: attachedLists = [], refetch: refetchLists } = useQuery({
    queryKey: ['campaign-lists', id],
    queryFn: () => campaignsSvc.getCampaignLists(Number(id)),
    enabled: !!id,
  });

  // ── Available lists for picker ──
  const { data: allLists = [] } = useQuery({
    queryKey: ['email-entry-lists'],
    queryFn: () => listEntryLists('email'),
  });

  // ── Contact/exclude counts ──
  const { data: contactCount = 0, refetch: refetchContactCount } = useQuery({
    queryKey: ['campaign-contact-count', id],
    queryFn: () => campaignsSvc.getCampaignContactCount(Number(id)),
    enabled: !!id,
  });
  const { data: excludeCount = 0, refetch: refetchExcludeCount } = useQuery({
    queryKey: ['campaign-exclude-count', id],
    queryFn: () => campaignsSvc.getCampaignExcludeCount(Number(id)),
    enabled: !!id,
  });

  // ── Recipient count (unique) — always freshly calculated, shows spinner while loading ──
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [recipientLoading, setRecipientLoading] = useState(true);
  const refreshRecipientCount = useCallback(async () => {
    if (!id) return;
    setRecipientLoading(true);
    try {
      const c = await campaignsSvc.getRecipientCount(Number(id));
      setRecipientCount(c);
    } catch { /* ignore */ }
    setRecipientLoading(false);
  }, [id]);

  useEffect(() => {
    if (campaign) refreshRecipientCount();
  }, [campaign, refreshRecipientCount]);

  // Refresh all counts when returning from contact/exclude subpages (matches legacy visibilitychange)
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && id) {
        refetchContactCount();
        refetchExcludeCount();
        refreshRecipientCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [id, refetchContactCount, refetchExcludeCount, refreshRecipientCount]);

  // ── Form state ──
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [description, setDescription] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [emailTemplateId, setEmailTemplateId] = useState<number | null>(null);
  const [accountUsers, setAccountUsers] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [contentEmpty, setContentEmpty] = useState(true);
  const [hasUnsubscribe, setHasUnsubscribe] = useState(true);
  const editorReadyRef = useRef(false);
  const baselineRef = useRef<Record<string, string>>({});

  // ── Modal / editor state ──
  const [showTestModal, setShowTestModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const issuesRef = useRef<HTMLDivElement>(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [editorFullscreen, setEditorFullscreen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  // ── Populate form ──
  const [initialized, setInitialized] = useState(false);

  // Flags derived from the compiled email. Kept together so the three places
  // that re-read the editor's content can't drift apart.
  const syncContentFlags = useCallback((html: string | null | undefined) => {
    setContentEmpty(isHtmlContentEmpty(html));
    setHasUnsubscribe(hasUnsubscribeToken(html));
  }, []);

  useEffect(() => {
    setInitialized(false);
    setContentEmpty(true);
    setHasUnsubscribe(true);
    setFormDirty(false);
    editorReadyRef.current = false;
  }, [id]);

  useEffect(() => {
    if (campaign && senders.length > 0 && !initialized) {
      const resolvedName = campaign.name ?? '';
      const resolvedSubject = campaign.subject ?? '';
      const resolvedPreheader = campaign.preheader ?? '';
      const resolvedDescription = campaign.description ?? '';

      let resolvedFromEmail = campaign.fromEmail ?? '';
      let resolvedFromName = campaign.fromName ?? '';
      if (!resolvedFromEmail) {
        const defaultSender = senders.find(s => s.default);
        if (defaultSender) {
          resolvedFromEmail = defaultSender.email;
          resolvedFromName = defaultSender.senderName;
        }
      }

      setName(resolvedName);
      setSubject(resolvedSubject);
      setPreheader(resolvedPreheader);
      setDescription(resolvedDescription);
      setFromEmail(resolvedFromEmail);
      setFromName(resolvedFromName);
      setScheduledAt(campaign.scheduledAt ?? '');
      setEmailTemplateId(campaign.emailTemplateId ?? null);
      setAccountUsers(!!(campaign as any).accountUsers);
      syncContentFlags(campaign.emailBody);

      // Open the editor the campaign was authored with, seeded from its content.
      setEditorType(detectEditorType(campaign.json));
      setEditorInit({ html: campaign.emailBody || '', plain: campaign.plainContent || '', json: campaign.json ?? null });
      setEditorSeed((s) => s + 1);

      baselineRef.current = {
        name: resolvedName, subject: resolvedSubject, preheader: resolvedPreheader,
        description: resolvedDescription, fromEmail: resolvedFromEmail,
        scheduledAt: campaign.scheduledAt ?? '',
      };
      setInitialized(true);
      setFormDirty(false);
    }
  }, [campaign, senders, initialized]);

  // Ignore the editor's own settling right after it loads, so seeding content
  // doesn't read as a user edit. This has to live in its own effect: the
  // hydration effect above depends on `initialized` and sets it, so its cleanup
  // ran the moment it finished and cancelled the timer before it could fire —
  // leaving the editor permanently "not ready" and every edit ignored.
  useEffect(() => {
    if (!initialized) return;
    editorReadyRef.current = false;
    const timer = setTimeout(() => { editorReadyRef.current = true; }, 800);
    return () => clearTimeout(timer);
  }, [initialized]);

  // ── Unsaved changes tracking ──
  const hasUnsavedChanges = useMemo(() => {
    if (!campaign || !initialized) return false;
    if (formDirty) return true;
    const b = baselineRef.current;
    if (name !== b.name) return true;
    if (subject !== b.subject) return true;
    if (description !== b.description) return true;
    if (fromEmail !== b.fromEmail) return true;
    if (preheader !== b.preheader) return true;
    if (scheduledAt !== b.scheduledAt) return true;
    return false;
  }, [formDirty, campaign, initialized, name, subject, description, fromEmail, preheader, scheduledAt]);

  useUnsavedChangesGuard(hasUnsavedChanges);

  // ── Close overflow menu on outside click ──
  useEffect(() => {
    if (!showOverflowMenu) return;
    const handler = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showOverflowMenu]);

  // ── Close the send-issues popover on outside click ──
  useEffect(() => {
    if (!showIssues) return;
    const handler = (e: MouseEvent) => {
      if (issuesRef.current && !issuesRef.current.contains(e.target as Node)) {
        setShowIssues(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showIssues]);

  // ── Editor fullscreen change tracking ──
  const handleFullscreenChange = useCallback((isFs: boolean) => {
    setEditorFullscreen(isFs);
    if (!isFs) {
      setTimeout(() => {
        syncContentFlags(editorRef.current?.getContent() ?? '');
      }, 300);
    }
  }, [syncContentFlags]);

  // ── Editor content change tracking ──
  const formDirtyRef = useRef(false);
  formDirtyRef.current = formDirty;
  // getContent() compiles the whole email, and the editors report every
  // keystroke, so only the dirty flag is set inline — the empty-content check
  // trails behind it.
  const contentEmptyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEditorChange = useCallback(() => {
    if (!editorReadyRef.current) return;
    if (!formDirtyRef.current) setFormDirty(true);
    if (contentEmptyTimerRef.current) clearTimeout(contentEmptyTimerRef.current);
    contentEmptyTimerRef.current = setTimeout(() => {
      syncContentFlags(editorRef.current?.getContent() ?? '');
    }, 400);
  }, [syncContentFlags]);

  useEffect(() => () => {
    if (contentEmptyTimerRef.current) clearTimeout(contentEmptyTimerRef.current);
  }, []);

  const handleTemplateMetadata = useCallback((tmpl: FtpTemplateRow) => {
    if (tmpl.subject && !subject) setSubject(tmpl.subject);
    if (tmpl.preheader && !preheader) setPreheader(tmpl.preheader);
    if (tmpl.from_name && !fromName) setFromName(tmpl.from_name);
    if (tmpl.from_email && !fromEmail) setFromEmail(tmpl.from_email);
    if (tmpl.id) setEmailTemplateId(Number(tmpl.id));
    setContentEmpty(false);
    if (!formDirtyRef.current) setFormDirty(true);
  }, [subject, preheader, fromName, fromEmail]);

  // ── Choose Template: editor + content follow the picked template ──
  const handleTemplatePicked = useCallback((tmpl: FtpTemplateRow) => {
    setShowTemplatePicker(false);
    const json = tmpl.json_content ?? null;
    setEditorType(detectEditorType(json));
    setEditorInit({ html: tmpl.html_content || '', plain: tmpl.plain_content || '', json });
    setEditorSeed((s) => s + 1);
    handleTemplateMetadata(tmpl);
  }, [handleTemplateMetadata]);

  // ── Start-from-scratch: pick DnD vs HTML, then mount a blank editor ──
  const handleEditorChoice = useCallback((type: EmailEditorType) => {
    setShowEditorChoice(false);
    setEditorType(type);
    setEditorInit({ html: '', plain: '', json: null });
    setEditorSeed((s) => s + 1);
    setContentEmpty(false);
    if (!formDirtyRef.current) setFormDirty(true);
    // Jump into the full editor so a blank email is immediately buildable.
    setTimeout(() => editorRef.current?.openFullscreen(), 100);
  }, []);

  // ── Build save payload helper ──
  const buildSavePayload = useCallback(() => {
    const htmlContent = editorRef.current?.getContent() ?? '';
    const plainContent = editorRef.current?.getPlainContent() ?? '';
    const jsonContent = editorRef.current?.getJson() ?? null;

    return {
      name: name || 'Untitled Campaign',
      subject,
      preheader,
      description,
      from_name: fromName,
      from_email: fromEmail,
      reply_to: fromEmail,
      html_content: htmlContent,
      plain_content: plainContent,
      json_content: jsonContent,
      scheduled_at: scheduledAt || null,
      email_template_id: emailTemplateId,
      accountUsers: accountUsers ? 1 : 0,
    };
  }, [name, subject, preheader, description, fromName, fromEmail, scheduledAt, emailTemplateId, accountUsers]);

  // ── Save mutation ──
  const saveMut = useMutation({
    mutationFn: async () => {
      await apiClient.patch(`/campaigns/${id}`, buildSavePayload());
    },
    onSuccess: () => {
      toast.success('Campaign saved');
      setFormDirty(false);
      baselineRef.current = { name, subject, preheader, description, fromEmail, scheduledAt };
      editorRef.current?.markSaved();
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign-editor', id] });
    },
    onError: () => toast.error('Failed to save campaign'),
  });

  const handleSave = useCallback(() => { saveMut.mutate(); }, [saveMut]);

  const handleEditorSave = useCallback((_html: string, _plain: string, _json: string | null) => {
    handleSave();
  }, [handleSave]);

  const handleBack = useCallback(() => {
    navigate('/marketing/email-campaigns');
  }, [navigate]);

  // ── Delete campaign ──
  const deleteMut = useMutation({
    mutationFn: () => campaignsSvc.deleteCampaign(Number(id)),
    onSuccess: () => {
      toast.success('Campaign deleted');
      navigate('/marketing/email-campaigns', { replace: true });
    },
    onError: () => toast.error('Failed to delete campaign'),
  });
  const handleDelete = useCallback(() => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) return;
    deleteMut.mutate();
  }, [deleteMut]);

  // ── Clone campaign ──
  const cloneMut = useMutation({
    mutationFn: (cloneName: string) => campaignsSvc.cloneCampaign(Number(id), cloneName),
    onSuccess: (data) => {
      toast.success('Campaign cloned successfully');
      setShowCloneModal(false);
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      navigate(`/marketing/campaigns/${data.id}`);
    },
    onError: () => toast.error('Failed to clone campaign'),
  });

  // ── Toggle account users ──
  const [accountUsersToggling, setAccountUsersToggling] = useState(false);
  const handleToggleAccountUsers = useCallback(async (checked: boolean) => {
    if (!id) return;
    setAccountUsers(checked);
    setAccountUsersToggling(true);
    setRecipientLoading(true);
    try {
      const res = await campaignsSvc.toggleAccountUsers(Number(id), checked);
      setRecipientCount(res.total_recipients);
    } catch {
      setAccountUsers(!checked);
      toast.error('Failed to toggle');
    }
    setAccountUsersToggling(false);
    setRecipientLoading(false);
  }, [id, toast]);

  // ── List management ──
  const [listFilter, setListFilter] = useState<'static' | 'dynamic'>('static');
  const [listSearch, setListSearch] = useState('');
  const [showListPicker, setShowListPicker] = useState(false);

  const attachedListIds = useMemo(() => new Set(attachedLists.map((l: CampaignList) => l.emailListId)), [attachedLists]);

  const filteredAvailable = useMemo(() => {
    let list = allLists.filter((l: AvailableList) => !attachedListIds.has(l.emailListId));
    list = list.filter((l: AvailableList) => l.listType === listFilter);
    if (listSearch.trim()) {
      const q = listSearch.toLowerCase();
      list = list.filter((l: AvailableList) => l.name.toLowerCase().includes(q));
    }
    return list;
  }, [allLists, attachedListIds, listFilter, listSearch]);

  const addListMut = useMutation({
    mutationFn: (listId: number) => {
      setRecipientLoading(true);
      return campaignsSvc.addCampaignList(Number(id), listId);
    },
    onSuccess: (data) => {
      refetchLists();
      if (data?.total_recipients != null) setRecipientCount(data.total_recipients);
      setRecipientLoading(false);
    },
    onError: () => {
      refetchLists();
      toast.error('Failed to add list');
      refreshRecipientCount();
    },
  });

  const removeListMut = useMutation({
    mutationFn: (listId: number) => {
      setRecipientLoading(true);
      return campaignsSvc.removeCampaignList(Number(id), listId);
    },
    onSuccess: (data) => {
      refetchLists();
      if (data?.total_recipients != null) setRecipientCount(data.total_recipients);
      setRecipientLoading(false);
    },
    onError: () => {
      refetchLists();
      toast.error('Failed to remove list');
      refreshRecipientCount();
    },
  });

  // ── Schedule floor ──
  // Re-derived on a timer so a date that was valid when picked starts failing
  // once it slips into the past, rather than staying enabled until reload.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (!scheduledAt) return;
    const t = setInterval(() => setNowTick(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, [scheduledAt]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const minScheduleAt = useMemo(() => nowAsDateTimeValue(userTz), [userTz, nowTick]);
  const scheduleInPast = !!scheduledAt && scheduledAt < minScheduleAt;

  // ── Send/schedule validation ──
  // Blockers stop the send; suggestions are things the send pipeline copes with
  // on its own but that are usually a mistake to ship.
  const sendIssues = useMemo(() => {
    const blockers: string[] = [];
    const suggestions: string[] = [];

    if (hasUnsavedChanges) blockers.push('You have unsaved changes — save them before sending.');
    if (!name.trim()) blockers.push('The campaign needs a name.');
    if (!subject.trim()) blockers.push('The email needs a subject line.');
    if (!fromName.trim() || !fromEmail.trim()) blockers.push('The campaign needs a sender address.');
    if ((recipientCount ?? 0) <= 0) blockers.push('This campaign has no recipients.');
    if (contentEmpty) blockers.push('The email has no content.');
    if (scheduleInPast) blockers.push('The scheduled send date is in the past.');

    if (!preheader.trim()) suggestions.push('No preheader — inboxes will preview the opening line of the email instead.');
    if (!hasUnsubscribe) suggestions.push('No unsubscribe link — one will be added to the bottom of the email for you.');

    return { blockers, suggestions };
  }, [hasUnsavedChanges, name, subject, fromName, fromEmail, recipientCount, contentEmpty, scheduleInPast, preheader, hasUnsubscribe]);

  const canSendOrSchedule = sendIssues.blockers.length === 0 && !recipientLoading;
  const issueCount = sendIssues.blockers.length + sendIssues.suggestions.length;
  const hasBlockers = sendIssues.blockers.length > 0;

  const sendLabel = scheduledAt ? 'Schedule Now' : 'Send Now';

  // ── Loading / redirect ──
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white gap-4">
        <p className="text-slate-500">Campaign not found.</p>
        <button type="button" onClick={() => navigate('/marketing/email-campaigns')} className="text-sm text-blue-600 hover:underline">
          Back to Campaigns
        </button>
      </div>
    );
  }

  // A scheduled campaign is locked: it gets a read-only page whose only action
  // is Cancel Send, so nobody can slip an edit in before it fires unattended.
  if (campaign.status === 'scheduled') {
    navigate(`/marketing/campaigns/${id}/scheduled`, { replace: true });
    return null;
  }
  if (campaign.status !== 'draft') {
    navigate(`/marketing/campaigns/${id}/stats`, { replace: true });
    return null;
  }

  // ── Editor access level (parity with legacy edit.php) ──
  // Permission 21 = "Email Editor Full Access", Permission 20 = "Mass E-mail with SendGrid"
  const isLevel5 = (authUser?.userLevel ?? 0) >= 5;
  const hasFullEditorPerm = hasPermission(21);
  const hasSendGridPerm = hasPermission(20);

  const editorAccessLevel: 'full' | 'locked' =
    (isLevel5 || hasFullEditorPerm) && !viewAsLocked ? 'full' : 'locked';

  if (!isLevel5 && !hasFullEditorPerm && !hasSendGridPerm) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white gap-4">
        <Lock className="h-10 w-10 text-slate-400" />
        <p className="text-slate-700 font-medium">You do not have permission to access the email editor.</p>
        <p className="text-sm text-slate-500">Contact an administrator to request access.</p>
        <button type="button" onClick={() => navigate('/marketing/email-campaigns')} className="text-sm text-blue-600 hover:underline">
          Back to Campaigns
        </button>
      </div>
    );
  }

  const isEditable = campaign.status === 'draft';

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      {/* ── Header bar ── */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
        <div className="flex items-center gap-3">
          <button type="button" onClick={handleBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <div className="h-5 w-px bg-gray-200" />
          <h1 className="text-base font-semibold text-gray-800 truncate max-w-xs">
            Edit Campaign: {campaign.name || 'Untitled'}
          </h1>

          {/* Status badge (only drafts reach this page) */}
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-[#e9ecef] text-[#495057] border border-[#dee2e6]">
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </span>

          {hasUnsavedChanges && (
            <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* View as Locked toggle (level 5 only) */}
          {isLevel5 && (
            <button
              type="button"
              onClick={() => setViewAsLocked(v => !v)}
              className={`inline-flex items-center justify-center rounded-md p-1.5 transition-colors ${
                viewAsLocked
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title={viewAsLocked ? 'Exit locked view — return to full access' : 'View as Locked — preview how editors with limited access see this campaign'}
              aria-label={viewAsLocked ? 'Exit locked view' : 'View as locked'}
              aria-pressed={viewAsLocked}
            >
              {viewAsLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            </button>
          )}

          {/* Send Test */}
          <button
            type="button"
            onClick={() => setShowTestModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Mail className="h-4 w-4" />
            Send Test
          </button>

          {/* Why this campaign can't (or maybe shouldn't) be sent yet */}
          {initialized && issueCount > 0 && (
            <div className="relative" ref={issuesRef}>
              <button
                type="button"
                onClick={() => setShowIssues(v => !v)}
                title={hasBlockers
                  ? `${sendIssues.blockers.length} thing${sendIssues.blockers.length === 1 ? '' : 's'} to fix before sending`
                  : `${sendIssues.suggestions.length} suggestion${sendIssues.suggestions.length === 1 ? '' : 's'} before you send`}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-medium transition-colors ${
                  hasBlockers
                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
              >
                <AlertTriangle className="h-4 w-4" />
                {issueCount}
              </button>

              {showIssues && (
                <div className="absolute right-0 top-full z-30 mt-1.5 w-80 rounded-md border border-gray-200 bg-white p-3 text-left shadow-lg">
                  {hasBlockers && (
                    <div className="mb-1">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-700">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Must fix before sending
                      </div>
                      <ul className="mt-1.5 space-y-1.5">
                        {sendIssues.blockers.map(msg => (
                          <li key={msg} className="flex gap-1.5 text-sm text-gray-700">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                            {msg}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {sendIssues.suggestions.length > 0 && (
                    <div className={hasBlockers ? 'mt-3 border-t border-gray-100 pt-3' : ''}>
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                        <Lightbulb className="h-3.5 w-3.5" />
                        Suggested
                      </div>
                      <ul className="mt-1.5 space-y-1.5">
                        {sendIssues.suggestions.map(msg => (
                          <li key={msg} className="flex gap-1.5 text-sm text-gray-700">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                            {msg}
                          </li>
                        ))}
                      </ul>
                      {!hasBlockers && (
                        <p className="mt-2 text-xs text-gray-400">
                          These won't stop the send — you can go ahead if that's intended.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Send Now / Schedule Now */}
          <button
            type="button"
            onClick={() => setShowReviewModal(true)}
            disabled={!canSendOrSchedule}
            title={hasBlockers ? 'Check the warnings beside this button' : undefined}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {scheduledAt ? <Calendar className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {sendLabel}
          </button>

          {/* Save */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saveMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4" />
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>

          {/* Overflow menu */}
          <div className="relative" ref={overflowRef}>
            <button
              type="button"
              onClick={() => setShowOverflowMenu(p => !p)}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {showOverflowMenu && (
              <div className="absolute right-0 z-50 mt-1 w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => { setShowOverflowMenu(false); setShowCloneModal(true); }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Copy className="h-4 w-4" />
                  Clone Campaign
                </button>
                {campaign.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => { setShowOverflowMenu(false); handleDelete(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Campaign
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Split panel ── */}
      <div className="flex flex-1 min-h-0">
        {/* Left: details form + contacts */}
        <div className="w-[42%] min-w-[400px] max-w-[560px] overflow-y-auto border-r border-gray-200 bg-white">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Email Campaign Details
            </h2>
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-blue-500" />
              {recipientLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
              ) : (
                <span className="text-lg font-bold text-blue-600">
                  {(recipientCount ?? 0).toLocaleString()}
                </span>
              )}
              <span className="text-gray-500">contacts</span>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Campaign Name */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Campaign Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} disabled={!isEditable}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="e.g., January Newsletter" />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)} disabled={!isEditable}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Internal notes about this campaign..." />
              <p className="mt-1 text-xs text-gray-400">For internal reference only — not visible to recipients</p>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Subject Line *</label>
              <input type="text" value={subject} onChange={e => setSubject(e.target.value)} disabled={!isEditable}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Email subject line" />
            </div>

            {/* Preheader */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Preheader</label>
              <input type="text" value={preheader} onChange={e => setPreheader(e.target.value)} maxLength={100} disabled={!isEditable}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Preview text shown after the subject line in the inbox" />
              <p className="mt-1 text-xs text-gray-400">
                Preview text shown after the subject line in most email clients. Ideal length is 40 to 100 characters.
              </p>
            </div>

            {/* From Email / From Name */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">From Email *</label>
                <div className="relative">
                  <select value={fromEmail} onChange={e => {
                    setFromEmail(e.target.value);
                    const s = senders.find(s => s.email === e.target.value);
                    if (s) setFromName(s.senderName || '');
                  }} disabled={!isEditable}
                    className="w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none bg-white pr-8 disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">-- Select sender email --</option>
                    {senders.map(s => (
                      <option key={s.emailAccountSenderID} value={s.email}>{s.email} ({s.senderName})</option>
                    ))}
                    {fromEmail && !senders.some(s => s.email === fromEmail) && (
                      <option value={fromEmail} className="text-red-500">{fromEmail} (Not in Account Senders)</option>
                    )}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">From Name</label>
                <input type="text" value={fromName} readOnly
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-gray-50 text-gray-500 outline-none cursor-default"
                  placeholder="Selected from sender" />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <label htmlFor="email-schedule-at" className="block text-sm font-medium text-gray-600 mb-1">
                <Calendar className="inline h-4 w-4 mr-1 text-gray-400" />
                Schedule Send <span className="font-normal text-gray-400">({tzAbbr})</span>
              </label>
              {/* `min` keeps past days and periods out of reach; scheduleInPast still
                  has to be checked, since a valid time drifts into the past while the
                  page sits open. */}
              <DateTimePicker
                id="email-schedule-at"
                value={scheduledAt}
                min={minScheduleAt}
                onChange={setScheduledAt}
                disabled={!isEditable}
                error={scheduleInPast ? 'That time has already passed. Pick a later one to schedule this campaign.' : undefined}
                hint="Leave empty to send manually. Set a date and time to schedule the campaign."
              />
            </div>

            {/* ── Campaign Contacts Section ── */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              {/* Direct Contacts */}
              <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#f0f7ff' }}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold" style={{ color: '#00aff0' }}>{contactCount}</span>
                    <span className="text-sm text-gray-600">direct contact(s)</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">Added directly to this campaign</p>
                </div>
                <Link to={`/marketing/campaigns/${id}/contacts`} className="text-sm font-medium text-blue-600 hover:underline">
                  View / Edit
                </Link>
              </div>

              {/* Contact Lists */}
              <div className="px-4 py-3 border-t border-gray-200 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl font-bold text-indigo-500">{attachedLists.length}</span>
                  <span className="text-sm text-gray-600">contact lists</span>
                </div>
                <p className="text-xs text-gray-400 mb-3">Attach reusable lists to include their members when sending</p>

                {/* Filter buttons */}
                <div className="flex items-center gap-1 mb-2">
                  <button type="button" onClick={() => setListFilter('static')}
                    className="rounded-full px-3 py-1 text-xs font-medium transition-colors border"
                    style={listFilter === 'static'
                      ? { background: '#e3f2fd', borderColor: '#1565c0', color: '#1565c0' }
                      : { background: '#f3f4f6', borderColor: '#d1d5db', color: '#6b7280' }
                    }>
                    Static
                  </button>
                  <button type="button" onClick={() => setListFilter('dynamic')}
                    className="rounded-full px-3 py-1 text-xs font-medium transition-colors border"
                    style={listFilter === 'dynamic'
                      ? { background: '#fff3e0', borderColor: '#e65100', color: '#e65100' }
                      : { background: '#f3f4f6', borderColor: '#d1d5db', color: '#6b7280' }
                    }>
                    Dynamic
                  </button>
                </div>

                {/* Add list picker */}
                {isEditable && (
                  <div className="relative mb-2">
                    <button type="button" onClick={() => setShowListPicker(p => !p)}
                      className="w-full flex items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                      <span>+ Attach a list</span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    {showListPicker && (
                      <div className="absolute left-0 right-0 z-20 mt-1 max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                        <div className="sticky top-0 bg-white p-2 border-b border-gray-100">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input type="text" value={listSearch} onChange={e => setListSearch(e.target.value)} placeholder="Search lists..."
                              className="w-full rounded border border-gray-200 pl-7 pr-3 py-1.5 text-xs focus:border-blue-400 outline-none" />
                          </div>
                        </div>
                        {filteredAvailable.length === 0 ? (
                          <p className="p-3 text-xs text-gray-400 text-center">No matching lists</p>
                        ) : (
                          filteredAvailable.map((l: AvailableList) => (
                            <button key={l.emailListId} type="button"
                              onClick={() => { addListMut.mutate(l.emailListId); setShowListPicker(false); setListSearch(''); }}
                              className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-indigo-50 transition-colors"
                            >
                              <span className="text-gray-700">{l.name}</span>
                              <span className="inline-flex items-center gap-1.5 text-xs">
                                <span className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                                  style={l.listType === 'static'
                                    ? { background: '#e3f2fd', color: '#1565c0' }
                                    : { background: '#fff3e0', color: '#e65100' }
                                  }>
                                  {l.listType}
                                </span>
                                <span className="text-gray-400">{(l.uniqueEmailCount ?? l.patientCount).toLocaleString()} emails</span>
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Attached lists */}
                {attachedLists.length > 0 && (
                  <div className="space-y-1">
                    {attachedLists.map((l: CampaignList) => (
                      <div key={l.emailListId} className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">{l.name}</span>
                          <span className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                            style={l.listType === 'static'
                              ? { background: '#e3f2fd', color: '#1565c0' }
                              : { background: '#fff3e0', color: '#e65100' }
                            }>
                            {l.listType}
                          </span>
                          <span className="text-xs text-gray-400">{(l.uniqueEmailCount ?? l.patientCount).toLocaleString()} emails</span>
                        </div>
                        {isEditable && (
                          <button type="button" onClick={() => removeListMut.mutate(l.emailListId)}
                            className="text-gray-400 hover:text-red-500 transition-colors">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Exclude Contacts */}
              <div className="px-4 py-3 border-t border-gray-200" style={{ background: '#fff8f8' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-red-500">{excludeCount}</span>
                      <span className="text-sm text-gray-600">excluded contact(s)</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">These emails will be excluded from sending</p>
                  </div>
                  <Link to={`/marketing/campaigns/${id}/excludes`} className="text-sm font-medium text-blue-600 hover:underline">
                    View / Edit
                  </Link>
                </div>
              </div>

              {/* Exclude Account Users */}
              <div className="px-4 py-3 border-t border-gray-200 bg-white">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={accountUsers} onChange={e => handleToggleAccountUsers(e.target.checked)}
                    disabled={!isEditable}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-400" />
                  <span className="text-sm text-gray-700">Exclude account users from this campaign</span>
                  {accountUsersToggling && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                </label>
                <p className="mt-1 ml-6 text-xs text-gray-400">
                  When checked, emails belonging to users on this account will be automatically excluded when sending
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-400 leading-relaxed">
              Direct contacts and list members will be merged when sending. Excluded contacts and account users (if selected) are removed before send.
            </p>
          </div>
        </div>

        {/* Right: Editor preview + empty overlay */}
        <div className="flex-1 min-w-0 bg-[#1e1e2e] relative">
          {/* Editor host — HTML or Drag & Drop; locked users always get locked HTML */}
          {initialized ? (
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>}>
              <EmailEditorHost
                key={`${id}-${editorType}-${editorSeed}`}
                ref={editorRef}
                editorType={editorType}
                accessLevel={editorAccessLevel}
                initialHtml={editorInit.html}
                initialPlainContent={editorInit.plain}
                initialJson={editorInit.json}
                onSave={handleEditorSave}
                onChange={handleEditorChange}
                onTemplateApply={handleTemplateMetadata}
                onFullscreenChange={handleFullscreenChange}
                previewMode
              />
            </Suspense>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          )}

          {/* Empty content overlay */}
          {initialized && contentEmpty && !editorFullscreen && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#1e1e2e]/80 backdrop-blur-sm">
              <div className="text-center max-w-sm">
                <MailOpen className="h-14 w-14 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Email Content is Empty</h3>
                {editorAccessLevel === 'locked' ? (
                  <>
                    <p className="text-sm text-slate-400 mb-6">Choose a template to get started</p>
                    <button type="button" onClick={() => setShowTemplatePicker(true)}
                      className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
                      Choose Template
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-400 mb-6">Start building your email from scratch or choose a template</p>
                    <div className="flex items-center justify-center gap-3">
                      <button type="button" onClick={() => setShowTemplatePicker(true)}
                        className="rounded-md border border-slate-500 px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors">
                        Choose Template
                      </button>
                      <button type="button"
                        onClick={() => setShowEditorChoice(true)}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
                        Start Building
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Editor choice + template picker (start-from-scratch / choose template) ── */}
      <EmailEditorChoiceModal
        isOpen={showEditorChoice}
        onClose={() => setShowEditorChoice(false)}
        onChoose={handleEditorChoice}
      />
      <FtpTemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        mode="select"
        onApply={() => {}}
        onSelect={(t) => handleTemplatePicked(t as FtpTemplateRow)}
      />

      {/* ── Test Email Modal ── */}
      {showTestModal && (
        <TestEmailModal
          campaignId={Number(id)}
          editorRef={editorRef}
          campaign={{ subject, preheader, fromEmail, fromName }}
          onClose={() => setShowTestModal(false)}
        />
      )}

      {/* ── Review Before Send Modal ── */}
      {showReviewModal && (
        <ReviewBeforeSendModal
          campaignId={Number(id)}
          editorRef={editorRef}
          recipientCount={recipientCount ?? 0}
          isSchedule={!!scheduledAt}
          scheduledAt={scheduledAt}
          campaign={{ name, subject, preheader, fromEmail, fromName, description }}
          issues={sendIssues}
          buildPayload={buildSavePayload}
          onClose={() => setShowReviewModal(false)}
          onSent={(scheduled) => {
            setShowReviewModal(false);
            qc.invalidateQueries({ queryKey: ['campaign-editor', id] });
            qc.invalidateQueries({ queryKey: ['campaigns'] });
            if (scheduled) {
              // Scheduling persists the form payload too, so nothing is lost.
              // Land on the read-only scheduled page (banner + Cancel Send)
              // rather than the list, matching the SMS flow.
              navigate(`/marketing/campaigns/${id}/scheduled`, { replace: true });
              return;
            }
            navigate('/marketing/email-campaigns', { replace: true });
          }}
        />
      )}

      <CloneCampaignModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={(cloneName) => cloneMut.mutate(cloneName)}
        loading={cloneMut.isPending}
      />

    </div>
  );
}

// ─── Test Email Modal Component ─────────────────────────────────────────────

function TestEmailModal({
  campaignId,
  editorRef,
  campaign,
  onClose,
}: {
  campaignId: number;
  editorRef: React.RefObject<EmailEditorHostHandle | null>;
  campaign: { subject: string; preheader: string; fromEmail: string; fromName: string };
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [testEmail, setTestEmail] = useState(user?.email ?? '');
  const toast = useToast();

  const sendMut = useMutation({
    mutationFn: () => {
      const htmlContent = editorRef.current?.getContent() ?? '';
      const plainContent = editorRef.current?.getPlainContent() ?? '';
      return campaignsSvc.sendTestEmail(campaignId, {
        test_email: testEmail,
        subject: campaign.subject,
        preheader: campaign.preheader,
        from_email: campaign.fromEmail,
        from_name: campaign.fromName,
        html_content: htmlContent,
        plain_content: plainContent,
      });
    },
    onSuccess: () => {
      toast.success(`Test email sent to ${testEmail}`);
      onClose();
    },
    onError: () => toast.error('Failed to send test email'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-800">Send Test Email</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">
          <label className="block text-sm font-medium text-gray-600 mb-1">Recipient Email</label>
          <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
            placeholder="Enter email address..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none" />
          <p className="mt-2 text-xs text-gray-400">
            A test copy of this campaign will be sent to the address above. The subject will be prefixed with [TEST].
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button type="button" onClick={() => sendMut.mutate()}
            disabled={!testEmail.trim() || sendMut.isPending}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {sendMut.isPending ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Review Before Send Modal Component ─────────────────────────────────────

function ReviewBeforeSendModal({
  campaignId,
  editorRef,
  recipientCount,
  isSchedule,
  scheduledAt,
  campaign,
  issues,
  buildPayload,
  onClose,
  onSent,
}: {
  campaignId: number;
  editorRef: React.RefObject<EmailEditorHostHandle | null>;
  recipientCount: number;
  isSchedule: boolean;
  scheduledAt: string;
  campaign: { name: string; subject: string; preheader: string; fromEmail: string; fromName: string; description: string };
  issues: { blockers: string[]; suggestions: string[] };
  buildPayload: () => Record<string, any>;
  onClose: () => void;
  /** `scheduled` is true when the campaign was scheduled rather than sent now. */
  onSent: (scheduled: boolean) => void;
}) {
  const toast = useToast();
  const userTz = useUserTimezone();
  // Compiled once on open — getContent() rebuilds the whole email, and nothing
  // can edit it while this modal is up.
  const [htmlContent] = useState(() => editorRef.current?.getContent() ?? '');

  /**
   * srcdoc has no document URL, so any relative image path in the email would
   * fail to resolve and collapse to its alt box. A <base> restores them.
   */
  const previewDoc = useMemo(() => {
    if (!htmlContent) return '';
    const base = `<base href="${window.location.origin}/">`;
    if (/<head[^>]*>/i.test(htmlContent)) return htmlContent.replace(/<head([^>]*)>/i, `<head$1>${base}`);
    if (/<html[^>]*>/i.test(htmlContent)) return htmlContent.replace(/<html([^>]*)>/i, `<html$1><head>${base}</head>`);
    return `<!DOCTYPE html><html><head>${base}</head><body style="margin:0">${htmlContent}</body></html>`;
  }, [htmlContent]);

  const sendMut = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      if (isSchedule) {
        return campaignsSvc.scheduleCampaign(campaignId, {
          ...payload,
          scheduled_at: scheduledAt,
        } as any);
      }
      return campaignsSvc.sendCampaign(campaignId, payload as any);
    },
    onSuccess: (data) => {
      toast.success(data?.message || (isSchedule ? 'Campaign scheduled' : 'Campaign sent'));
      onSent(isSchedule);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || 'Failed';
      toast.error(msg);
    },
  });

  const initial = campaign.fromName?.charAt(0)?.toUpperCase() || 'E';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex w-full max-w-7xl h-[85vh] rounded-lg bg-white shadow-xl overflow-hidden">
        {/* Left side — fixed width so the preview keeps the rest */}
        <div className="w-[380px] shrink-0 flex flex-col overflow-y-auto border-r border-gray-200">
          <div className="p-5 flex-1 space-y-4">
            {/* Contact count */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Users className="h-4 w-4 text-blue-500" />
              <span className="font-semibold text-lg text-gray-800">{recipientCount.toLocaleString()}</span>
              <span>contacts</span>
            </div>

            {/* Warning */}
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <strong>This action cannot be undone.</strong>{' '}
              {isSchedule
                ? 'Once scheduled, this campaign will be delivered to all recipients at the scheduled time.'
                : 'Once sent, this campaign will be delivered to all recipients immediately.'}
            </div>

            {/* Anything still outstanding, repeated here so it gets one last
                look at the point of no return. Blockers normally keep this
                modal from opening at all, but state can change while it's up
                (a schedule time slipping into the past, for one). */}
            {issues.blockers.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Must fix before sending
                </div>
                <ul className="mt-1.5 space-y-1">
                  {issues.blockers.map(msg => (
                    <li key={msg} className="flex gap-1.5 text-sm text-red-800">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-red-500" />
                      {msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {issues.suggestions.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  <Lightbulb className="h-3.5 w-3.5" />
                  Suggested — won't stop the send
                </div>
                <ul className="mt-1.5 space-y-1">
                  {issues.suggestions.map(msg => (
                    <li key={msg} className="flex gap-1.5 text-sm text-amber-900">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />
                      {msg}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Inbox preview */}
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-semibold uppercase text-gray-400 mb-3">Inbox Preview</p>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                  {initial}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800 truncate">{campaign.fromName || 'Sender'}</span>
                    <span className="text-xs text-gray-400">{campaign.fromEmail}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-700 truncate">{campaign.subject || '(No subject)'}</p>
                  <p className="text-xs text-gray-400 truncate">{campaign.preheader}</p>
                </div>
              </div>
            </div>

            {/* Summary table */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Campaign</span>
                <span className="text-gray-800 font-medium">{campaign.name || 'Untitled'}</span>
              </div>
              {isSchedule && scheduledAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Schedule</span>
                  <span className="text-gray-800 font-medium">{formatScheduleDate(scheduledAt, userTz)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={() => sendMut.mutate()}
              disabled={sendMut.isPending || issues.blockers.length > 0}
              title={issues.blockers.length > 0 ? 'Fix the items listed above first' : undefined}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isSchedule ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'
              }`}>
              {sendMut.isPending ? 'Processing…' : isSchedule ? 'Schedule Now' : 'Send Now'}
            </button>
          </div>
        </div>

        {/* Right side: email preview */}
        <div className="flex-1 flex flex-col min-w-0 bg-gray-100">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm font-semibold text-gray-600">
            Email Preview
          </div>
          {/* The viewport has to stay clear of the email's own mobile
              breakpoint, which the compiler sets at wrapper.maxWidth + 40 (680px
              for the default 600px email). Inside it, `img { max-width: 100%
              !important }` overrides every image's natural width and they fill
              their cell. So give the iframe a floor comfortably above that and
              let the email centre itself at its own width, exactly as the
              client-preview modal does — never pin it to a narrow column. */}
          <div className="flex-1 min-h-0 overflow-auto p-3">
            <div className="h-full min-w-[760px]">
              <iframe
                srcDoc={previewDoc}
                className="h-full w-full rounded border border-gray-200 bg-white shadow-sm"
                title="Email preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


